import React, {useEffect, useState, useCallback} from "react";
import "../MqttEvent/MqttEvent.css";
import Badge from "react-bootstrap/Badge";
import ReactJsonView from "react-json-view";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowDown, faArrowUp} from "@fortawesome/free-solid-svg-icons";
import {useTimezone} from "../../../../../../contexts/TimezoneContext";
import RemoteDetectionEvent from "./RemoteDetectionEvent";
import DeviceUpdateEvent from "./DeviceUpdateEvent";
import DeviceDeleteEvent from "./DeviceDeleteEvent";
import IntegrationsEvent from "./IntegrationsEvent";
import HomeEvent from "./HomeEvent";

const httpMethods = {0: "GET", 1: "POST", 2: "PUT", 3: "DELETE", 4: "PATCH"};

function GatewayMessageRow({ e, timezone }) {
    const [showJson, setShowJson] = useState(false);
    const innerEv = e?.payload?.event || e?.payload || {};
    const isUp = e.payload?.action === 'up' || innerEv.direction === 'gw-up';
    const ts = innerEv.timestamp || e.timestamp;
    const timeString = new Intl.DateTimeFormat('en-US', {
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        fractionalSecondDigits: 3, timeZone: timezone, hour12: false,
    }).format(new Date(ts));

    return (
        <div className="pt-2 d-flex flex-column">
            <div className="d-flex align-items-center">
                <Badge
                    className={"me-1 d-flex align-items-center justify-content-center " + (isUp ? "upMessage" : "downMessage")}
                    pill style={{fontSize: '0.75rem', minHeight: '30px'}}>
                    <FontAwesomeIcon icon={isUp ? faArrowUp : faArrowDown} size="sm"/>
                </Badge>
                <Badge bg={isUp ? "success" : "secondary"}
                    className={"me-1 d-flex align-items-center " + (isUp ? "upMessage" : "downMessage")}
                    style={{fontSize: '0.75rem', minHeight: '30px'}}>
                    {timeString}
                </Badge>
                <Badge
                    bg={showJson ? "secondary" : "dark"}
                    className="d-flex align-items-center justify-content-center json-toggle-btn"
                    style={{cursor: 'pointer', fontSize: '0.75rem', padding: '0.25rem 0.5rem', minHeight: '30px'}}
                    onClick={() => setShowJson(s => !s)}
                    title="Toggle JSON view"
                >
                    {'{}'}
                </Badge>
            </div>
            {showJson && (
                <ReactJsonView
                    src={e.payload?.event || e.payload}
                    className="mqttEventPayload"
                    theme="ocean"
                    borderStyle="none"
                    name={false}
                    displayObjectSize={false}
                    displayDataTypes={false}
                    collapsed={4}
                />
            )}
        </div>
    );
}

function UserEvent({event, eventsByRequestId = {}, bondsData = {}, homeMembers = [], homes = []}) {
    const {timezone} = useTimezone();
    const [isWaiting, setIsWaiting] = useState(false);
    const [isTimeout, setTimeout] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [showRaw, setShowRaw] = useState(false);

    // Live WS events: payload = { action, event: {i,m,s,...}, bond_id, path, ... }
    // Historic CloudWatch events: payload = { i, m, s, direction, topic, b, ... } (flat)
    const innerEvent = event?.payload?.event || event?.payload || {};
    const requestId = innerEvent.i?.toLowerCase();

    // Helper to get the inner MQTT payload from either event structure
    const getInnerEvent = (e) => e?.payload?.event || e?.payload || {};

    const getRelatedEvents = useCallback(() => {
        if (!requestId || !eventsByRequestId[requestId]) return [];
        return eventsByRequestId[requestId].filter(e => e.source === 'user');
    }, [requestId, eventsByRequestId]);

    const checkWaiting = useCallback(() => {
        if (!requestId) return;
        const related = getRelatedEvents();
        if (!related.length) return;

        const hasResponse = related.some(e => getInnerEvent(e).s);

        if (hasResponse) {
            setIsWaiting(false);
            setTimeout(false);
        } else {
            const ageInSeconds = (Date.now() - new Date(event.timestamp).getTime()) / 1000;
            if (ageInSeconds > 10) {
                setTimeout(true);
                setIsWaiting(false);
            } else {
                setIsWaiting(true);
                setTimeout(false);
            }
        }
    }, [event, getRelatedEvents, requestId]);

    useEffect(() => checkWaiting(), [event, checkWaiting]);

    useEffect(() => {
        let interval;
        if (isWaiting) {
            interval = setInterval(() => {
                checkWaiting();
                setRefreshKey(prev => prev + 1);
            }, 500);
        }
        return () => { if (interval) clearInterval(interval); };
    }, [isWaiting, checkWaiting]);

    if (!event || !event.payload) return null;

    // ── Known specialised renderers ────────────────────────────────────────────
    if (event.payload.type === 'CLOUDDEVICES') {
        return <RemoteDetectionEvent event={event} />;
    }

    if (event.payload.type === 'BOND' && event.payload.action === 'DEVICE_UPDATE') {
        return <DeviceUpdateEvent event={event} />;
    }

    if (event.payload.type === 'BOND' && (event.payload.action === 'DEVICE_ADD' || event.payload.action === 'DEVICE_DELETE')) {
        return <DeviceDeleteEvent event={event} />;
    }

    if (event.payload.type === 'INTEGRATIONS') {
        return <IntegrationsEvent event={event} bondsData={bondsData} />;
    }

    if (event.payload.type === 'HOME') {
        return <HomeEvent event={event} homeMembers={homeMembers} homes={homes} />;
    }

    // ── Unhandled event type — show raw expand button ──────────────────────────
    if (!requestId) {
        return (
            <div style={{maxWidth: '100%', overflow: 'hidden'}}>
                <Badge
                    bg={showRaw ? "secondary" : "dark"}
                    style={{cursor: 'pointer', fontSize: '0.75rem', userSelect: 'none'}}
                    onClick={() => setShowRaw(s => !s)}
                >
                    {showRaw ? '▲ hide' : '▼ raw'}
                </Badge>
                {showRaw && (
                    <div style={{marginTop: '6px', maxWidth: '100%', overflowX: 'auto'}}>
                        <ReactJsonView
                            src={event.payload}
                            className="mqttEventPayload"
                            theme="ocean"
                            borderStyle="none"
                            name={false}
                            displayObjectSize={false}
                            displayDataTypes={false}
                            collapsed={2}
                        />
                    </div>
                )}
            </div>
        );
    }

    // ── Gateway (mqtt/gw-down + gw-up) events ─────────────────────────────────
    function methodBadge() {
        const method = httpMethods[innerEvent.m];
        if (!method) return null;

        let variant = "success";
        if (method === "DELETE") variant = "danger";
        else if (["POST", "PUT", "PATCH"].includes(method)) variant = "primary";

        let hasFailed = isTimeout;
        if (!hasFailed) {
            hasFailed = getRelatedEvents().some(e => getInnerEvent(e).s >= 400);
        }

        return <Badge bg={variant} style={{fontSize: '0.75rem', ...(hasFailed && {border: '2px solid #dc3545'})}}>{method}</Badge>;
    }

    function statusBadge() {
        if (isWaiting) return <Badge bg="warning" pill className="me-1 text-black" style={{fontSize: '0.75rem'}}>Waiting</Badge>;
        if (isTimeout) return <Badge bg="danger" pill className="me-1 text-black" style={{fontSize: '0.75rem'}}>Timeout</Badge>;

        const response = getRelatedEvents().reduce((acc, e) => getInnerEvent(e).s || acc, null);
        if (!response) return null;
        if (response < 300) return <Badge bg="success" pill className="me-1" style={{fontSize: '0.75rem'}}>{response}</Badge>;
        if (response < 400) return <Badge bg="warning" pill className="me-1" style={{fontSize: '0.75rem'}}>{response}</Badge>;
        if (response < 600) return <Badge bg="danger" pill className="me-1" style={{fontSize: '0.75rem'}}>{response}</Badge>;
        return <Badge bg="secondary" pill className="me-1" style={{fontSize: '0.75rem'}}>{response}</Badge>;
    }

    function getMessageRequests() {
        const related = getRelatedEvents();
        if (!related.length) return null;

        const sorted = [...related].sort((a, b) => b.timestamp - a.timestamp);
        return sorted.map((e, index) => (
            <GatewayMessageRow key={index} e={e} timezone={timezone} />
        ));
    }

    return <div key={refreshKey} style={{maxWidth: '100%', overflow: 'hidden'}}>
        <div style={{display: 'flex', gap: '0.5rem', alignItems: 'flex-start', minWidth: 0}}>
            <div style={{flex: '1 1 50%', minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'flex-start'}}>
                {methodBadge()} {statusBadge()}
            </div>
            <div style={{flex: '1 1 50%', minWidth: 0}}>
                <input type="text" value={'/' + (event.payload?.path || '')} className="mqttEventTopic w-100" readOnly/>
            </div>
        </div>
        {getMessageRequests()}
    </div>;
}

export default UserEvent;
